import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useTrackToggle, type UseTrackToggleProps } from "@livekit/components-react";
import { Track, type ScreenShareCaptureOptions } from "livekit-client";
import { Mic, MicOff, MonitorUp, MonitorX, Video, VideoOff } from "lucide-react";

import MeetingControlMenu from "./MeetingControlMenu";

type ToggleSource =
    | Track.Source.Microphone
    | Track.Source.Camera
    | Track.Source.ScreenShare;

/** Each toggle's two labels, and the name of the menu beside it. */
const COPY: Record<ToggleSource, { on: string; off: string; menu: string }> = {
    [Track.Source.Microphone]: {
        on: "meetingControls.microphone",
        off: "meetingControls.microphoneMuted",
        menu: "meetingControls.options.microphone",
    },
    [Track.Source.Camera]: {
        on: "meetingControls.camera",
        off: "meetingControls.cameraOff",
        menu: "meetingControls.options.camera",
    },
    [Track.Source.ScreenShare]: {
        on: "meetingControls.screenShareStop",
        off: "meetingControls.screenShare",
        menu: "meetingControls.options.share",
    },
};

/**
 * Ask the browser for the audio playing in what is being shared.
 *
 * LiveKit defaults screen capture to `audio: false`, and Chrome only offers
 * the "share tab audio" checkbox when the constraints ask for audio at all —
 * so without this a therapist playing a video or a breathing exercise to a
 * patient sent them a silent picture. It is an offer, not a promise: the
 * person sharing still ticks the box, browsers that cannot capture audio
 * (Firefox, Safari) simply return no audio track, and LiveKit publishes a
 * `ScreenShareAudio` track only when one comes back.
 */
const SCREEN_SHARE_CAPTURE: ScreenShareCaptureOptions = { audio: true };

const ICON: Record<ToggleSource, { on: typeof Mic; off: typeof Mic }> = {
    [Track.Source.Microphone]: { on: Mic, off: MicOff },
    [Track.Source.Camera]: { on: Video, off: VideoOff },
    [Track.Source.ScreenShare]: { on: MonitorX, off: MonitorUp },
};

/**
 * A control in the call: the microphone, the camera, or sharing.
 *
 * Built on `useTrackToggle` rather than LiveKit's `TrackToggle` because that
 * component gives no way to reach the enabled state, so the only thing the old
 * control had to say "you are muted" was `lk-button`'s own background colour.
 * A therapist who cannot distinguish those two greys — or who simply glances —
 * could not tell whether their patient could hear them. Here the state is in
 * the icon and in the word as well as the colour (WCAG 1.4.1).
 *
 * `TrackToggle` also draws its own icon *and* renders its children, so passing
 * an Alchely icon through it put two icons in the button.
 *
 * `menu` is what the chevron beside the control opens: the input devices, and
 * whatever else belongs to this control rather than to the call — blurring for
 * the camera, sound-only for sharing. Grouping them under the thing they
 * modify is what let the bar go from eight controls to five.
 *
 * The `aria-pressed` is on `enabled`, so a screen reader reads the control the
 * same way the icon shows it.
 *
 * `compact` is the phone's version of the same control: the icon at the same
 * 44px target with the label read out rather than drawn. The label is what
 * carries the state, so it is never dropped — only moved into `sr-only`,
 * where the icon and the colour go on saying the same thing to everyone else.
 */
export default function MeetingToggle({
    source,
    menu,
    compact = false,
}: {
    source: ToggleSource;
    menu?: (close: () => void) => ReactNode;
    compact?: boolean;
} & Omit<UseTrackToggleProps<ToggleSource>, "source">) {
    const { t } = useTranslation();
    const { enabled, pending, buttonProps } = useTrackToggle({
        source,
        captureOptions:
            source === Track.Source.ScreenShare
                ? SCREEN_SHARE_CAPTURE
                : undefined,
    });

    const copy = COPY[source];
    const Icon = enabled ? ICON[source].on : ICON[source].off;
    const label = t(enabled ? copy.on : copy.off);

    /**
     * Whether being off is worth flagging.
     *
     * A muted microphone and a covered camera are states the other person is
     * living with and you cannot see; not sharing your screen is simply the
     * resting state of the call, and colouring it as a warning would cry wolf
     * for the whole session.
     */
    const flagWhenOff = source !== Track.Source.ScreenShare;

    const state = enabled
        ? flagWhenOff
            ? ""
            : "room-control-on"
        : flagWhenOff
          ? "room-control-off"
          : "";

    const button = (
        <button
            {...buttonProps}
            type="button"
            aria-pressed={enabled}
            disabled={pending}
            className={`room-control ${
                compact ? "room-control-icon" : "room-control-stack"
            } ${menu ? "" : state} disabled:opacity-50`}
        >
            <Icon size={18} aria-hidden="true" />
            <span className={compact ? "sr-only" : undefined}>{label}</span>
        </button>
    );

    if (!menu) return button;

    return (
        <div className={`room-control-group ${state}`}>
            {button}
            <MeetingControlMenu
                label={t(copy.menu)}
                testId={`options-${source}`}
                compact={compact}
            >
                {menu}
            </MeetingControlMenu>
        </div>
    );
}
